/**
 * Ensures a stable user_id across different hosts.
 * Priority:
 *   1) ?uid=&lt;32-hex&gt; in URL (persist + clean URL)
 *   2) cookie "promptly_uid" (sync to localStorage)
 *   3) localStorage "user_id" (create if missing)
 * Also sets a cookie for cross-subdomain persistence on *.redhotteam.app
 */
export function ensureUserId() {
  try {
    const KEY = 'user_id';
    const qs = new URLSearchParams(location.search);
    const forced = qs.get('uid');

    const setUidCookie = (uid) => {
      try {
        const attrs = [
          `promptly_uid=${uid}`,
          'Max-Age=31536000', // 1 year
          'Path=/',
          'SameSite=Lax',
        ];
        if (location.protocol === 'https:') attrs.push('Secure');
        // Share across subdomains when on *.redhotteam.app
        if (location.hostname.endsWith('.redhotteam.app')) {
          attrs.push('Domain=.redhotteam.app');
        }
        document.cookie = attrs.join('; ');
      } catch {
        // Silently ignore cookie errors on restrictive platforms
      }
    };

    // If URL override is present and valid, trust it and clean the URL
    if (forced && /^[a-f0-9]{32}$/i.test(forced)) {
      localStorage.setItem(KEY, forced);
      setUidCookie(forced);
      // remove ?uid= from the address bar without a reload
      try {
        const u = new URL(location.href);
        u.searchParams.delete('uid');
        history.replaceState(null, '', u.toString());
      } catch {
        // Silently ignore history API errors
      }
      return forced;
    }

    // Try cookie
    const m = document.cookie.match(/(?:^|;\s*)promptly_uid=([a-f0-9]{32})/i);
    if (m) {
      localStorage.setItem(KEY, m[1]);
      return m[1];
    }

    // Fallback to localStorage (create if missing)
    let id = localStorage.getItem(KEY);
    if (!id) {
      const arr = new Uint8Array(16);
      crypto.getRandomValues(arr);
      id = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(KEY, id);
      setUidCookie(id);
    }
    return id;
  } catch {
    return 'anonymous';
  }
}

/** Optional: make a shareable link that pins your uid in the URL */
export function makeShareLink() {
  const uid = (typeof localStorage !== 'undefined' && localStorage.getItem('user_id')) || '';
  const u = new URL(location.href);
  if (uid) u.searchParams.set('uid', uid);
  return u.toString();
}